/**
 * useScriptedContainer
 *
 * Bridges React with imperative vendor scripts that mutate the DOM.
 *
 * Pattern: vendor widgets like RealPage OLL, some Yardi embeds, Engrain
 * SightMap, etc. are self-executing — they read config from their own
 * <script src> query string, look up the target container once at load
 * time via getElementById, and have no init/destroy API. Letting React
 * own the container means StrictMode (or any parent re-render) destroys
 * the iframe the vendor injected and the one-shot script has no way to
 * re-hydrate it.
 *
 * This hook owns the real container as a detached DOM node in a
 * window-level singleton, keeps it alive across unmount/remount, and
 * moves it into whatever wrapper React currently has mounted. A small
 * cleanup delay lets StrictMode reclaim the whole thing instead of
 * reloading the widget.
 *
 * Returns a ref to attach to a thin React wrapper div — the hook
 * appends the real vendor container inside that wrapper.
 */

import { useEffect, useRef, type RefObject } from 'react';

const DEFAULT_CLEANUP_DELAY_MS = 100;
const REGISTRY_KEY = '__scriptedContainerRegistry';

type ContainerInstance = {
  cleanupTimer: number | null;
  mountCount: number;
  script: HTMLScriptElement | null;
  container: HTMLDivElement | null;
  src: string | null;
};

type ContainerRegistry = Map<string, ContainerInstance>;

function getRegistry(): ContainerRegistry {
  const win = window as Window & { [REGISTRY_KEY]?: ContainerRegistry };
  if (!win[REGISTRY_KEY]) {
    win[REGISTRY_KEY] = new Map();
  }
  return win[REGISTRY_KEY]!;
}

function getInstance(containerId: string): ContainerInstance {
  const registry = getRegistry();
  let inst = registry.get(containerId);
  if (!inst) {
    inst = {
      cleanupTimer: null,
      mountCount: 0,
      script: null,
      container: null,
      src: null,
    };
    registry.set(containerId, inst);
  }
  return inst;
}

export interface UseScriptedContainerOptions {
  /**
   * Unique DOM id for the vendor's target container. Must match whatever
   * id the vendor script will look up (typically passed to the script
   * itself as a query param).
   */
  containerId: string;
  /**
   * Fully-built script URL including any query params the vendor reads
   * at load time. Pass null to disable loading (e.g. required config
   * not yet available).
   */
  scriptSrc: string | null;
  /**
   * Delay in ms before tearing down on unmount. Needs to outlast
   * StrictMode's synchronous remount. Default 100.
   */
  cleanupDelayMs?: number;
}

export function useScriptedContainer(
  options: UseScriptedContainerOptions,
): RefObject<HTMLDivElement> {
  const { containerId, scriptSrc, cleanupDelayMs = DEFAULT_CLEANUP_DELAY_MS } = options;
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!scriptSrc || !wrapper) return;

    const inst = getInstance(containerId);
    inst.mountCount += 1;
    if (inst.cleanupTimer !== null) {
      window.clearTimeout(inst.cleanupTimer);
      inst.cleanupTimer = null;
    }

    // Own the container outside React's tree so the vendor-injected
    // iframe (or whatever) survives unmount/remount.
    if (!inst.container) {
      const container = document.createElement('div');
      container.id = containerId;
      container.style.width = '100%';
      container.style.height = '100%';
      inst.container = container;
    }
    if (inst.container.parentElement !== wrapper) {
      wrapper.appendChild(inst.container);
    }

    // Src changed: vendor scripts are one-shot, so tear the content
    // down and reload to re-bind against the new params.
    if (inst.src !== scriptSrc) {
      inst.script?.remove();
      inst.container.innerHTML = '';
      inst.script = null;
      inst.src = null;
    }

    if (!inst.script) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.type = 'text/javascript';
      script.setAttribute('data-scripted-container', containerId);
      document.body.appendChild(script);
      inst.script = script;
      inst.src = scriptSrc;
    }

    return () => {
      inst.mountCount = Math.max(0, inst.mountCount - 1);
      if (inst.mountCount > 0) return;

      inst.cleanupTimer = window.setTimeout(() => {
        const latest = getInstance(containerId);
        if (latest.mountCount > 0) return;
        latest.script?.remove();
        latest.container?.remove();
        latest.script = null;
        latest.container = null;
        latest.src = null;
        latest.cleanupTimer = null;
        getRegistry().delete(containerId);
      }, cleanupDelayMs);
    };
  }, [containerId, scriptSrc, cleanupDelayMs]);

  return wrapperRef;
}
